import React, {useContext, useMemo, useState} from 'react';
import {BootstrapTable, TableHeaderColumn} from 'react-bootstrap-table';
import I18nMessage from '../../components/I18nMessage';
import {useIntl} from 'react-intl';
import 'moment/locale/pt-br';
import moment from 'moment';
import styled from 'styled-components';
import './sinister.sass';
import {Modal, ModalBody, ModalFooter, ModalHeader} from 'reactstrap';
import Accordion from '../../components/accordion';
import {getColor, getSinisterTypeI18N} from '../../helpers/sinister-help';
import Button from '../../components/button';
import history from '../../routes/history';
import {notificationClickState} from '../../helpers/notificationClick';
import {sinisterReport} from '../../services/reportService';
import FileSaver from 'file-saver';
import {ThemeContext} from '../../apiContexts';
import {ThemedTableWrapper} from '../../components/dataTable/style';
import {computeColumnWidths, toProportionalPercentages} from '../../helpers/columnWidth';
import useStore from '../../hooks/use-store';
import {canSeeAuditFields} from '../../helpers/auth';
import useTimezone from '../../hooks/use-timezone';
import {formatDateTimeColumn} from '../../helpers/excel-helper';

const ModalStyled = styled(Modal)`
   top: 20%;
   .modal-dialog{
      overflow:visible;
      min-height:100%!important;
      height:600px !important;
   }
   .modal-content{
        background: #FFFFFF;
        height: 60%;
        box-shadow: 2px 4px 16px rgba(0, 0, 0, 0.15);
        border-radius: 12px;
    }
`;

const ModalHeaderStyled = styled(ModalHeader)`
    background: #FFFFFF;
    border-bottom: 0;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    color: #16A5DA;
    font-size: 16px;
`;

const ModalBodyStyled = styled(ModalBody)`
    height:100%;
`;

const AccordionStyled = styled(Accordion)`
  button.accordion{
    background-color: white;
  }
`;

const ModalFooterStyled = styled(ModalFooter)`
 border-top: 0px;
`;

const SinisterTable = (props) => {
    const {
        list,
    } = props;

    const intl = useIntl();
    const theme = useContext(ThemeContext);
    const {store} = useStore();
    const showAuditFields = canSeeAuditFields(store.auth.profile);
    const {timezone} = useTimezone();
    const [modal, setModal] = useState(false);
    const [rowSelected, setSelectedRow] = useState(null);

    const toggle = () => setModal(!modal);

    // Uma função só pra cada formatação, usada tanto no dataFormat exibido quanto no cálculo de
    // largura: se divergissem, a largura ficaria errada sem nenhum teste pra acusar.
    const formatOpeningDate = (cell) => moment(cell).format(intl.formatMessage({id: 'sinisterOpeningDateFormat'}));
    const formatStatusLabel = (status) => intl.formatMessage(
        {id: 'sinisterStatus' + status.charAt(0).toUpperCase() + status.slice(1)});

    // Largura por conteúdo, mesmo mecanismo de src/components/dataTable/style.js -- calculada
    // sobre a lista inteira, não só a página visível. "Ações" fica de fora do cálculo por
    // conteúdo (são só ícones, sem texto pra medir) mas entra na proporção final como as demais.
    // Não espelha página/busca via onPageChange/afterSearch em options: options é recriado
    // inline a cada render e a lib trata qualquer mudança de identidade como estado novo, o que
    // entraria em loop de re-render. Ver mesmo comentário em dataTable/index.jsx.
    const ACTIONS_COLUMN_WIDTH = 110;
    const columnWidths = useMemo(() => ({
        ...computeColumnWidths([
            {key: 'number', title: intl.formatMessage({id: 'sinisterNumber'}), rows: list, pick: (row) => row.number},
            {key: 'devicePlate', title: intl.formatMessage({id: 'sinisterDevicePlate'}), rows: list, pick: (row) => row.devicePlate},
            {key: 'deviceModel', title: intl.formatMessage({id: 'sinisterDeviceModel'}), rows: list, pick: (row) => row.deviceModel},
            {
                key: 'openingDate',
                title: intl.formatMessage({id: 'sinisterOpeningDate'}),
                rows: list,
                pick: (row) => row.openingDate,
                format: formatOpeningDate,
            },
            {
                key: 'status',
                title: intl.formatMessage({id: 'sinisterStatus'}),
                rows: list,
                pick: (row) => row.status,
                format: formatStatusLabel,
            },
            ...(showAuditFields ? [
                {key: 'creatorName', title: intl.formatMessage({id: 'sharedCreatedBy'}), rows: list, pick: (row) => row.creatorName},
                {key: 'creatorEmail', title: intl.formatMessage({id: 'sharedCreatorEmail'}), rows: list, pick: (row) => row.creatorEmail},
                {
                    key: 'createdAtFormatted',
                    title: intl.formatMessage({id: 'sharedCreatedAt'}),
                    rows: list,
                    pick: (row) => row.createdAt,
                    format: (value) => formatDateTimeColumn(value, timezone),
                },
                {
                    key: 'updatedAtFormatted',
                    title: intl.formatMessage({id: 'sharedUpdatedAt'}),
                    rows: list,
                    pick: (row) => row.updatedAt,
                    format: (value) => formatDateTimeColumn(value, timezone),
                },
            ] : []),
        ]),
        deviceId: ACTIONS_COLUMN_WIDTH,
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [list, intl, showAuditFields, timezone]);
    const tableMinWidth = Object.values(columnWidths).reduce((sum, width) => sum + width, 0);
    // O card da tela (Content, admin-form-container.jsx) não encolhe pelo conteúdo -- a tabela
    // sempre ocupa 100% dele; o que muda é como os 100% se dividem entre as colunas (proporcional
    // ao px calculado acima). Mesmo raciocínio de dataTable/index.jsx.
    const columnPercentages = toProportionalPercentages(columnWidths);

    const selectRowProp = {
        mode: 'radio',
        bgColor: (row, isSelect) => {
            if (isSelect && row.id === 0) {
                return null;
            }
            // return '#2497e9';
            return null;
        },
        hideSelectColumn: true,
        clickToSelect: true,
        onSelect: (row, isSelected, e) => {
            if (!row) {
                if (isSelected && row.id === 0) {
                    return false;
                }
                setSelectedRow(row);
            }
            return true;
        },
    };

    const showSinterDetail = () => {
        if (rowSelected) {
            toggle();
        }
    };

    const exportReport = async (data) => {
        try {
            const response = await sinisterReport(data);

            if (response) {
                const blob = new Blob([response], {
                    type:
                        'application/pdf',
                });
                FileSaver.saveAs(blob, intl.formatMessage({id: 'sinisterPdfFileName'}));
            };
        } catch (err) {
            console.log(err);
        }
        return;
    };

    const showOccurrenceDetail = (occurrence) => {
        return <>
         <span className={'occurrence-modal-item'}>
                        <span
                            className={'occurrence-modal-label'}>{intl.formatMessage({id: 'sharedDate'})}: </span> {moment(occurrence.occurrenceDate).format(intl.formatMessage({id: 'dateFormat'}))}
                    </span>
            <span className={'occurrence-modal-item'}>
                        <span
                            className={'occurrence-modal-label'}>{intl.formatMessage({id: 'sinisterTimeLabel'})} </span> {moment(occurrence.occurrenceDate).format('hh:mm')}
                    </span>
            <span className={'occurrence-modal-item'}>
                        <span
                            className={'occurrence-modal-label'}>{intl.formatMessage({id: 'deviceCategory'})}: </span> {intl.formatMessage({id: getSinisterTypeI18N(occurrence.category)})}
                    </span>
            <span className={'occurrence-modal-item'}>
                        <span className={'occurrence-modal-label'}>{intl.formatMessage({id: 'sharedAddress'})}: </span> {occurrence.address}
                    </span>
            <span className={'occurrence-modal-item'}>
                        <span className={'occurrence-modal-label'}>{intl.formatMessage({id: 'sinisterOccurrenceText'})} </span> {occurrence.description}
                    </span>
        </>;
    };

    return (
        <>
            <ModalStyled isOpen={modal} toggle={toggle} className={'className'}>
                <ModalHeaderStyled toggle={toggle}>{intl.formatMessage({id: 'sinisterSummary'})}</ModalHeaderStyled>
                <ModalBodyStyled>
                    <AccordionStyled
                        title={intl.formatMessage({id: 'sinisterEssentialInfoTitle'})}
                        component={<div>
                            <p className={'info-text'}><span
                                className={'info-text-label'}>{intl.formatMessage({id: 'sinisterDevicePlate'})}:</span> {rowSelected && rowSelected.devicePlate ? rowSelected.devicePlate : ''}
                            </p>
                            <p className={'info-text'}><span
                                className={'info-text-label'}>{intl.formatMessage({id: 'sinisterDeviceModel'})}:</span> {rowSelected && rowSelected.deviceModel ? rowSelected.deviceModel : ''}
                            </p>
                            <p className={'info-text'}><span
                                className={'info-text-label'}>{intl.formatMessage({id: 'sinisterDeviceColor'})}:</span> {rowSelected && rowSelected.deviceColor ? rowSelected.deviceColor : ''}
                            </p>
                            <p className={'info-text'}><span
                                className={'info-text-label'}>{intl.formatMessage({id: 'sharedAddress'})}:</span> {rowSelected && rowSelected.openingAddress ? rowSelected.openingAddress : ''}
                            </p>
                            <p className={'info-text'}><span
                                className={'info-text-label'}>Latitude:</span> {rowSelected && rowSelected.openingLatitude ? rowSelected.openingLatitude : ''}
                            </p>
                            <p className={'info-text'}><span
                                className={'info-text-label'}>Longitude:</span> {rowSelected && rowSelected.openingLongitude ? rowSelected.openingLongitude : ''}
                            </p>
                            <p className={'info-text'}><span
                                className={'info-text-label'}>{intl.formatMessage({id: 'deviceContact'})}:</span> {rowSelected && rowSelected.customerName ? rowSelected.customerName : ''}
                            </p>
                            <p className={'info-text'}><span
                                className={'info-text-label'}>{intl.formatMessage({id: 'sinisterMobile'})}:</span> {rowSelected && rowSelected.customerPhone ? rowSelected.customerPhone : ''}
                            </p>
                            <p className={'info-text'}><span
                                className={'info-text-label'}>{intl.formatMessage({id: 'sinisterTypeLabel'})}:</span> {rowSelected && rowSelected.openingType ? rowSelected.openingType : ''}
                            </p>
                            <p className={'info-text'}><span
                                className={'info-text-label'}>{intl.formatMessage({id: 'sinisterInfoLabel'})}</span> {rowSelected && rowSelected.openingDescription ? rowSelected.openingDescription : ''}
                            </p>
                        </div>}
                    />
                    {rowSelected && rowSelected.occurrences && rowSelected.occurrences.map((occurrence) =>
                        <AccordionStyled
                            key={rowSelected.id + occurrence.id}
                            title={intl.formatMessage({id: 'sinisterOccurrenceTitle'}, {type: intl.formatMessage({id: getSinisterTypeI18N(occurrence.category)})})}
                            component={showOccurrenceDetail(occurrence)}
                        />)
                    }
                    {rowSelected && rowSelected.status !== 'opened' &&
                    <AccordionStyled
                        title={intl.formatMessage({id: 'sinisterClosingTitle'})}
                        component={<div>
                            <p className={'info-text'}><span
                                className={'info-text-label'}>{intl.formatMessage({id: 'sharedAddress'})}:</span> {rowSelected && rowSelected.closingAddress ? rowSelected.closingAddress : ''}
                            </p>
                            <p className={'info-text'}><span
                                className={'info-text-label'}>Latitude:</span> {rowSelected && rowSelected.closingLatitude ? rowSelected.closingLatitude : ''}
                            </p>
                            <p className={'info-text'}><span
                                className={'info-text-label'}>Longitude:</span> {rowSelected && rowSelected.closingLongitude ? rowSelected.closingLongitude : ''}
                            </p>
                            <p className={'info-text'}><span
                                className={'info-text-label'}>{intl.formatMessage({id: 'sinisterClosingType'})}:</span> {rowSelected && rowSelected.closingType ?
                                intl.formatMessage({id: 'sinisterType' + rowSelected.closingType.charAt(0).toUpperCase() + rowSelected.closingType.slice(1)}) : ''}
                            </p>
                            <p className={'info-text'}><span
                                className={'info-text-label'}>{intl.formatMessage({id: 'sinisterInfoLabel'})}</span> {rowSelected && rowSelected.closingDescription ? rowSelected.closingDescription : ''}
                            </p>
                        </div>}
                    />}
                </ModalBodyStyled>
                <ModalFooterStyled>
                    <Button className={'outline'}
                        size={'sm'}
                        color={'theme'}
                        onClick={(_) => exportReport(rowSelected.id)}>
                            <i className="fas fa-arrow-down"/> {intl.formatMessage({id: 'sinisterDownloadReport'})}
                    </Button>
                </ModalFooterStyled>
            </ModalStyled>
            <ThemedTableWrapper theme={theme}>
                <BootstrapTable
                    options={{
                        onRowMouseOver: (row) => {
                            if (row !== rowSelected) {
                                setSelectedRow(row);
                            }
                        },
                        noDataText: intl.formatMessage({id: 'sharedNoDataText'}),
                        sizePerPageList: [{
                            text: '10', value: 10,
                        }, {
                            text: '50', value: 50,
                        }, {
                            text: intl.formatMessage({id: 'sharedAll'}), value: list.length,
                        }],
                        sizePerPage: 10, // which size per page you want to locate as default
                        prePage: intl.formatMessage({id: 'sharedPrev'}), // Previous page button text
                        nextPage: intl.formatMessage({id: 'sharedNext'}), // Next page button text
                        firstPage: intl.formatMessage({id: 'sharedFirst'}), // First page button text
                        lastPage: intl.formatMessage({id: 'sharedLast'}), // Last page button text
                    }}
                    data={list}
                    search={true}
                    keyField={'id'}
                    selectRow={selectRowProp}
                    tableStyle={{'--table-min-width': `${tableMinWidth}px`}}
                    striped
                    hover
                    pagination
                >
                    {[
                        <TableHeaderColumn key='number' dataField='number' width={columnPercentages.number}><I18nMessage id={'sinisterNumber'}/></TableHeaderColumn>,
                        <TableHeaderColumn key='devicePlate' dataField='devicePlate' width={columnPercentages.devicePlate}><I18nMessage id={'sinisterDevicePlate'}/></TableHeaderColumn>,
                        <TableHeaderColumn key='deviceModel' dataField='deviceModel' width={columnPercentages.deviceModel}><I18nMessage id={'sinisterDeviceModel'}/></TableHeaderColumn>,
                        <TableHeaderColumn key='openingDate' dataField='openingDate'
                                           width={columnPercentages.openingDate}
                                           dataFormat={(cell, row) => formatOpeningDate(cell)}>
                            <I18nMessage id={'sinisterOpeningDate'}/>
                        </TableHeaderColumn>,
                        <TableHeaderColumn key='status' dataField='status' width={columnPercentages.status} dataFormat={(cell, row) => <span
                            className={`status sinister-color-${getColor(row)}`}>
                        {formatStatusLabel(cell)}</span>}>
                            <I18nMessage id={'sinisterStatus'}/>
                        </TableHeaderColumn>,
                        ...(showAuditFields ? [
                            <TableHeaderColumn key='creatorName' dataField='creatorName' width={columnPercentages.creatorName}><I18nMessage id={'sharedCreatedBy'}/></TableHeaderColumn>,
                            <TableHeaderColumn key='creatorEmail' dataField='creatorEmail' width={columnPercentages.creatorEmail}><I18nMessage id={'sharedCreatorEmail'}/></TableHeaderColumn>,
                            <TableHeaderColumn key='createdAtFormatted' dataField='createdAtFormatted' width={columnPercentages.createdAtFormatted}><I18nMessage id={'sharedCreatedAt'}/></TableHeaderColumn>,
                            <TableHeaderColumn key='updatedAtFormatted' dataField='updatedAtFormatted' width={columnPercentages.updatedAtFormatted}><I18nMessage id={'sharedUpdatedAt'}/></TableHeaderColumn>,
                        ] : []),
                        <TableHeaderColumn key='deviceId' export={false} dataField='deviceId' width={columnPercentages.deviceId}
                                           dataFormat={(cell, row) => <div className="buttons">
                                               <div className="action_btn">
                                                   <button name={'btnShowDetail'} onClick={() => showSinterDetail()}
                                                           className="btn btn-outline" type="button"><i
                                                       className={'fad fa-eye'}/></button>
                                                   {row.status === 'opened' ?
                                                       <button
                                                           onClick={() => history.push(`/admin/events/devices/${row.deviceId}`, notificationClickState())}
                                                           className="btn btn-outline" type="button"><i
                                                           className={'fad fa-shield'}/></button> :
                                                       <button
                                                            onClick={(_) => exportReport(row.id)}
                                                            className="btn btn-outline" type="button"><i
                                                            className={'fad fa-arrow-down'}/></button>
                                                   }
                                               </div>
                                           </div>}><I18nMessage id={'sinisterActions'}/></TableHeaderColumn>,
                    ]}
                </BootstrapTable>
            </ThemedTableWrapper>
        </>
    );
};

export default SinisterTable;
